import { useAuth0 } from '@auth0/auth0-react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useQueryClient } from '@tanstack/react-query'
import { useContext, useEffect } from 'react'
import { useNavigate } from 'react-router'
import AppContext from '@/context/appContext'
import { useMonitoringPeriodContext } from '@/context/monitoringPeriodContext'
import { queryKeys } from '@/hooks/data/queryKeys'
import { useAssignedFarms } from '@/hooks/data/useAssignedFarms'
import { useFarmFilterOptions } from '@/hooks/data/useFarmFilterOptions'
import { useAssignedFarmsFilters } from '@/hooks/useAssignedFarmsFilters'
import { useMeasuredHeight } from '@/hooks/useMeasuredHeight'
import { selectFarm } from '@/hooks/useSelectedFarm'
import { getFarm, type User as FarmMappingUser } from '@/services/farmMapping/getFarms'
import getProgramConfig from '@/services/ruutsApi/getProgramConfig'
import type { AssignedFarm } from '@/types/apiModels'
import { processLoading, type ProcessLoadingProps } from '@/utils/Loading/processLoading'
import { messages } from '@/utils/messages/index'
import { displayErrorMessageModes } from '@/components/GlobalLoadingModal/GlobalLoadingModal'
import FarmFilters from './FarmFilters'
import FarmsTable, { computeTableMinHeight } from './FarmsTable'

const EMPTY_FILTER_OPTIONS = { programs: [], hubs: [] }
const CARD_BORDER = '1px solid #e2e7ee'

const FarmSelector = () => {
  const { setLoadingModalConfig, userRoleSelected } = useContext(AppContext)
  const { checkForBlockedMonitoringPeriods } = useMonitoringPeriodContext()
  const { user, isAuthenticated } = useAuth0()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [setFiltersElement, filtersHeight] = useMeasuredHeight()

  const filters = useAssignedFarmsFilters()
  const { page: currentPage, setPage } = filters
  const queriesEnabled = Boolean(isAuthenticated && userRoleSelected)

  const {
    data: filterOptions,
    isLoading: isFilterOptionsLoading,
    isError: isFilterOptionsError,
  } = useFarmFilterOptions({
    enabled: queriesEnabled,
  })

  const {
    data: farmsPage,
    isLoading: isFarmsLoading,
    isFetching: isFarmsFetching,
    isError: isFarmsError,
    refetch: refetchFarms,
  } = useAssignedFarms(filters.apiQuery, { enabled: queriesEnabled })

  // A stale deep link or a result set that shrank between visits can point past the last page;
  // this corrects it once loaded data proves the requested page is out of range.
  useEffect(() => {
    if (!farmsPage) return
    const { totalPages } = farmsPage.pagination
    if (totalPages > 0 && currentPage > totalPages) {
      setPage(totalPages)
    }
  }, [farmsPage, currentPage, setPage])

  const { programId, hubId, setProgramId, setHubId } = filters

  // An out-of-scope id shows as "Todos" but would stay in the query; only loaded options can tell it apart.
  useEffect(() => {
    if (!filterOptions || isFilterOptionsLoading || isFilterOptionsError) return
    if (
      programId !== undefined &&
      !filterOptions.programs.some(program => program.id === programId)
    ) {
      setProgramId(undefined)
    }
    if (hubId !== undefined && !filterOptions.hubs.some(hub => hub.id === hubId)) {
      setHubId(undefined)
    }
  }, [
    filterOptions,
    isFilterOptionsLoading,
    isFilterOptionsError,
    programId,
    hubId,
    setProgramId,
    setHubId,
  ])

  const handleFarmSelect = async (chosenFarm: AssignedFarm) => {
    await processLoading({
      setLoadingModalConfig,
      loadingMessage: messages.getMessage(messages.keys.FORM_LOADING_FARM_MESSAGE),
      successfulMessage: '',
      // processLoading reads `.detail`, which the inferred ContactSupportReason type omits.
      contactReasonSupportType: {
        title: messages.getMessage(messages.keys.ERROR_FARM_WITH_MULTIPLE_DEALS_AND_PROGRAMS_TITLE),
        detail: messages.getMessage(
          messages.keys.ERROR_FARM_WITH_MULTIPLE_DEALS_AND_PROGRAMS_DETAIL,
        ),
      } as ProcessLoadingProps['contactReasonSupportType'],
      displayErrorMessageMode: displayErrorMessageModes.dialog,
      errorMessage: messages.getMessage(
        messages.keys.ERROR_FARM_WITH_MULTIPLE_DEALS_AND_PROGRAMS_MESSAGE,
      ),
      displayErrorCustomMessage: false,
      doAction: async () => {
        // A farm with multiple deals/programs throws here: it is not usable, so the selection is
        // left untouched and the error surfaces as the descriptive dialog configured above.
        const farm = await getFarm({ id: chosenFarm.id, user: user as unknown as FarmMappingUser })
        const programConfig = await getProgramConfig({ farmId: farm.id })

        // Both responses are the complete fresh state for their key, so seed them before the
        // selection lands and the boot chain re-reads what this flow already fetched.
        queryClient.setQueryData(queryKeys.farm(farm.id), farm)
        queryClient.setQueryData(queryKeys.programConfig(farm.id), programConfig)

        selectFarm(farm.id)
        await checkForBlockedMonitoringPeriods({
          farmId: farm.id,
          blockedMonitoringPeriods: programConfig?.blockedMonitoringPeriods,
        })

        navigate('/farms')
      },
    })
  }

  const farmsCount = farmsPage?.items.length ?? 0

  return (
    <Stack alignItems="center" direction="column" display="flex" flexGrow={1} mt={4} spacing={3}>
      <Stack flexShrink={0}>
        <Typography textAlign="center" variant="h6">
          <b>Establecimientos Asignados</b>
        </Typography>
        <Typography textAlign="center" variant="caption">
          Seleccioná el establecimiento con el que vas a trabajar
        </Typography>
      </Stack>

      {userRoleSelected && (
        // A content-hugging card can't pass a smaller min-height up through CSS.
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: '1 1 0',
            minHeight: filtersHeight + computeTableMinHeight(farmsCount),
            width: '100%',
            maxWidth: 1100,
            px: { xs: 2, sm: 3 },
          }}
        >
          <Box
            aria-label="Establecimientos"
            component="section"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: '0 1 auto',
              maxHeight: '100%',
              minHeight: 0,
              backgroundColor: '#fff',
              border: CARD_BORDER,
              borderRadius: '8px',
              // Matches the prototype's --shadow token.
              boxShadow: '0 1px 3px rgba(16,24,40,.08), 0 8px 24px rgba(16,24,40,.04)',
              overflow: 'hidden',
            }}
          >
            <Box ref={setFiltersElement} sx={{ flexShrink: 0, borderBottom: CARD_BORDER }}>
              <FarmFilters
                hubId={filters.hubId}
                options={filterOptions ?? EMPTY_FILTER_OPTIONS}
                optionsLoading={isFilterOptionsLoading}
                programId={filters.programId}
                search={filters.searchInput}
                status={filters.status}
                onClear={filters.clearFilters}
                onHubIdChange={filters.setHubId}
                onProgramIdChange={filters.setProgramId}
                onSearchChange={filters.setSearchInput}
                onStatusChange={filters.setStatus}
              />
            </Box>
            <FarmsTable
              farms={farmsPage?.items ?? []}
              isError={isFarmsError}
              isFetching={isFarmsFetching}
              isLoading={isFarmsLoading}
              page={filters.page}
              pageSize={filters.apiQuery.size}
              queryKey={JSON.stringify(queryKeys.assignedFarms(filters.apiQuery))}
              sort={filters.sort}
              total={farmsPage?.pagination.total ?? 0}
              totalPages={farmsPage?.pagination.totalPages ?? 1}
              onPageChange={filters.setPage}
              onRetry={refetchFarms}
              onSelect={handleFarmSelect}
              onToggleSort={filters.toggleSort}
            />
          </Box>
        </Box>
      )}
    </Stack>
  )
}

export default FarmSelector
